/**
 * Operator hotel setup — view/edit a partner hotel's curated operational facts
 * (spec 48). Same fact editor as the platform's hotel-setup page, but on the
 * cross-tenant operator surface: reads need `admin:cross_tenant_read` (route-level),
 * edits `admin:hotel_configure` (hostesa_admin only) and every write is audited
 * on behalf of the partner. Facts split into "Required for activation" (the
 * onboarding gate's four) and "Good to have".
 */
import {
  type HotelFact,
  type HotelFactsResponse,
  useDeleteOperatorHotelFact,
  useOperatorHotelFacts,
  useUpsertOperatorHotelFact,
} from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  ErrorState,
  Input,
  Label,
  LoadingState,
  StatusBadge,
  Textarea,
} from '@hostesa/ui';
import { useMemo, useState } from 'react';

const SECTION_EYEBROW =
  'mb-3 flex items-center gap-2.5 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)] before:h-px before:w-[18px] before:bg-[var(--color-line)]';

// Categories whose values are free-form prose get a Textarea; the rest a single-line Input.
const TEXTAREA_CATEGORIES = new Set(['cancellation_policy', 'location_directions', 'pets_policy']);

// Fallback label for a category the backend introduced ahead of this bundle —
// `t()` on an unregistered key would THROW (i18n missingKeyHandler).
function humanizeCategory(category: string): string {
  const spaced = category.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function HotelSetupSection({ hotelId }: { hotelId: string }) {
  const { t } = useT('backoffice');
  const factsQuery = useOperatorHotelFacts(hotelId);

  return (
    <section data-testid="hotel-setup-section" className="mb-6">
      <p className={SECTION_EYEBROW}>{t('oversight.hotelSetup.title')}</p>
      <p className="mb-4 text-sm text-[var(--color-stone-mid)]">
        {t('oversight.hotelSetup.description')}
      </p>
      {factsQuery.isLoading ? (
        <LoadingState data-testid="hotel-setup-loading" title={t('oversight.hotelSetup.loading')} />
      ) : factsQuery.isError || !factsQuery.data ? (
        <ErrorState
          data-testid="hotel-setup-error"
          title={t('oversight.hotelSetup.errorTitle')}
          description={t('oversight.hotelSetup.errorDescription')}
        />
      ) : (
        <HotelSetupFacts key={hotelId} hotelId={hotelId} data={factsQuery.data} />
      )}
    </section>
  );
}

function HotelSetupFacts({ hotelId, data }: { hotelId: string; data: HotelFactsResponse }) {
  const { t, i18n } = useT('backoffice');
  const { can } = useAuth();
  const editable = can('admin:hotel_configure');

  const labelOf = (category: string) =>
    i18n.exists(`oversight.hotelSetup.categories.${category}.label`, { ns: 'backoffice' })
      ? t(`oversight.hotelSetup.categories.${category}.label`)
      : humanizeCategory(category);
  const descriptionOf = (category: string) =>
    i18n.exists(`oversight.hotelSetup.categories.${category}.description`, { ns: 'backoffice' })
      ? t(`oversight.hotelSetup.categories.${category}.description`)
      : '';

  const factByCategory = useMemo(() => {
    const map = new Map<string, HotelFact>();
    for (const f of data.facts) map.set(f.category, f);
    return map;
  }, [data.facts]);

  // Local input values, seeded once from the first data load (key-remounted per hotel).
  const [values, setValues] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const f of data.facts) seed[f.category] = f.value;
    return seed;
  });

  const required = data.requiredCategories;
  const goodToHave = data.allowedCategories.filter((c) => !required.includes(c));
  const allRequiredSet = required.length > 0 && required.every((c) => factByCategory.has(c));

  const setValue = (category: string, value: string) =>
    setValues((prev) => ({ ...prev, [category]: value }));

  const rowProps = (category: string) => ({
    category,
    hotelId,
    multiline: TEXTAREA_CATEGORIES.has(category),
    label: labelOf(category),
    description: descriptionOf(category),
    serverFact: factByCategory.get(category),
    value: values[category] ?? '',
    editable,
    onChange: (v: string) => setValue(category, v),
    onCleared: () => setValue(category, ''),
    t,
  });

  return (
    <div className="flex flex-col gap-4">
      {!editable && (
        <p data-testid="hotel-setup-readonly" className="text-sm text-[var(--color-stone-mid)]">
          {t('oversight.hotelSetup.readOnlyNote')}
        </p>
      )}
      {editable && (
        <p
          data-testid="hotel-setup-audit-note"
          role="note"
          className="text-sm text-[var(--color-stone-mid)]"
        >
          {t('oversight.hotelSetup.auditNote')}
        </p>
      )}

      <Card data-testid="hotel-setup-required" className="p-4 sm:p-5">
        <div className="mb-4 flex items-center gap-3">
          <h2 className="font-[var(--font-display)] text-lg text-[var(--color-charcoal)]">
            {t('oversight.hotelSetup.required.title')}
          </h2>
          {allRequiredSet && (
            <StatusBadge
              data-testid="hotel-setup-activation-complete"
              status="success"
              label={t('oversight.hotelSetup.activationComplete')}
            />
          )}
        </div>
        <p className="mb-4 text-sm text-[var(--color-stone-mid)]">
          {t('oversight.hotelSetup.required.description')}
        </p>
        <CardContent className="flex flex-col gap-6 p-0">
          {required.map((category) => (
            <FactRow key={category} {...rowProps(category)} showClear={false} />
          ))}
        </CardContent>
      </Card>

      <Card data-testid="hotel-setup-good-to-have" className="p-4 sm:p-5">
        <h2 className="mb-1 font-[var(--font-display)] text-lg text-[var(--color-charcoal)]">
          {t('oversight.hotelSetup.goodToHave.title')}
        </h2>
        <p className="mb-4 text-sm text-[var(--color-stone-mid)]">
          {t('oversight.hotelSetup.goodToHave.description')}
        </p>
        <CardContent className="flex flex-col gap-6 p-0">
          {goodToHave.map((category) => (
            <FactRow key={category} {...rowProps(category)} showClear={true} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

interface FactRowProps {
  category: string;
  hotelId: string;
  multiline: boolean;
  label: string;
  description: string;
  serverFact: HotelFact | undefined;
  value: string;
  editable: boolean;
  showClear: boolean;
  onChange: (value: string) => void;
  onCleared: () => void;
  t: ReturnType<typeof useT>['t'];
}

function FactRow({
  category,
  hotelId,
  multiline,
  label,
  description,
  serverFact,
  value,
  editable,
  showClear,
  onChange,
  onCleared,
  t,
}: FactRowProps) {
  // Per-row mutation instances: pending/success/error state stays with THIS row
  // even when several rows save concurrently (shared instances misattributed it).
  const upsert = useUpsertOperatorHotelFact(hotelId);
  const del = useDeleteOperatorHotelFact(hotelId);
  const isSet = serverFact !== undefined;
  const savingThis = upsert.isPending;
  const clearingThis = del.isPending;
  const savedThis = upsert.isSuccess;
  const saveErrorThis = upsert.isError;
  const clearErrorThis = del.isError;

  const trimmed = value.trim();
  const unchanged = trimmed === (serverFact?.value ?? '');
  const busy = savingThis || clearingThis;
  const inputDisabled = !editable || busy;
  const saveDisabled = inputDisabled || unchanged || trimmed === '';

  const inputId = `fact-${category}`;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={inputId}>{label}</Label>
        <StatusBadge
          data-testid={`fact-status-${category}`}
          status={isSet ? 'success' : 'warning'}
          label={
            isSet ? t('oversight.hotelSetup.status.set') : t('oversight.hotelSetup.status.missing')
          }
        />
      </div>
      {description && <p className="text-xs text-[var(--color-stone-mid)]">{description}</p>}
      {multiline ? (
        <Textarea
          id={inputId}
          data-testid={`fact-input-${category}`}
          value={value}
          disabled={inputDisabled}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Input
          id={inputId}
          data-testid={`fact-input-${category}`}
          value={value}
          disabled={inputDisabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {editable && (
        <div className="flex items-center gap-3 pt-0.5">
          <Button
            type="button"
            size="sm"
            data-testid={`fact-save-${category}`}
            disabled={saveDisabled}
            onClick={() => upsert.mutate({ category, value: trimmed })}
          >
            {savingThis ? t('oversight.hotelSetup.saving') : t('oversight.hotelSetup.save')}
          </Button>
          {showClear && isSet && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              data-testid={`fact-clear-${category}`}
              disabled={inputDisabled}
              onClick={() => del.mutate({ category }, { onSuccess: onCleared })}
            >
              {clearingThis ? t('oversight.hotelSetup.clearing') : t('oversight.hotelSetup.clear')}
            </Button>
          )}
          {savedThis && (
            <span
              data-testid={`fact-saved-${category}`}
              className="text-sm text-[var(--color-success)]"
            >
              {t('oversight.hotelSetup.saved')}
            </span>
          )}
          {saveErrorThis && (
            <span
              data-testid={`fact-error-${category}`}
              className="text-sm text-[var(--color-alert)]"
            >
              {t('oversight.hotelSetup.saveError')}
            </span>
          )}
          {clearErrorThis && (
            <span
              data-testid={`fact-clear-error-${category}`}
              className="text-sm text-[var(--color-alert)]"
            >
              {t('oversight.hotelSetup.clearError')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
